import React, { useState, useRef, useCallback, ReactNode } from "react";
import { cn } from "../../styles";
import { LocalizationScope } from "../../util/LocalizationScope";
import { Typography } from "../../atoms";
import { ThemeColor } from "../../props";
import { processErrorContent } from "./helper";
import { BaseFormFieldProps, FormFieldConstraint } from "./index";
import { ButtonControl } from "./ButtonControl";
import { Chip } from "../Chip";

export interface DialogMultiFieldValue {
  values: Array<{ label: string; value: any }>;
  domain?: string | null;
}

export interface FormFieldDialogMultiProps extends BaseFormFieldProps {
  constraints: FormFieldConstraint;
  value: DialogMultiFieldValue | null;
  onChangeField: (
    id: string,
    value: DialogMultiFieldValue | null,
    opts?: any,
  ) => void;
  renderDialog: (ctx: {
    open: boolean;
    onClose: () => void;
    onClear: (() => void) | false;
    onSelect: (values: any, domain?: string) => void;
    selectedIds: Array<{ label: string; value: any }>;
  }) => ReactNode;
}

export const DialogMultiField: React.FC<FormFieldDialogMultiProps> = ({
  className,
  id,
  value,
  onChangeField,
  label = "Label",
  localKey,
  errors = [],
  touched,
  helper,
  helperKey,
  constraints,
  disabled,
  keepHeight,
  noLabel,
  dense,
  renderDialog,
  readOnly,
}) => {
  const [open, setOpen] = useState(false);
  const persistedValue = useRef<DialogMultiFieldValue | null>(null);

  const error = !open && touched && errors[0];

  const handleOpen = useCallback(() => {
    setOpen(true);
    persistedValue.current = value;
  }, [value]);

  const handleClose = useCallback(() => {
    setOpen(false);
    onChangeField(id, persistedValue.current);
  }, [id, onChangeField]);

  const handleClear = useCallback(() => {
    setOpen(false);
    onChangeField(id, null);
  }, [id, onChangeField]);

  const handleSelect = useCallback(
    (values: any, domain?: string) => {
      setOpen(false);
      if (!values) return;
      if (values.length === 0) {
        onChangeField(id, null);
        return;
      }
      onChangeField(id, { values, domain: domain || null });
    },
    [id, onChangeField],
  );

  const showClear = !disabled && !readOnly && !constraints.presence && !!value;

  return (
    <div className={className}>
      {!noLabel && (
        <div className="flex">
          <Typography.Label
            htmlFor={id}
            className={cn(
              "block font-medium select-none",
              dense ? "mb-0.5 text-xs" : "mb-1 text-sm",
            )}
            content={label || { local: id }}
          />
          {!!constraints.presence && (
            <span className="text-red-500 ml-0.5 text-xs align-super">*</span>
          )}
        </div>
      )}
      <ButtonControl
        onClick={disabled || readOnly ? undefined : handleOpen}
        disabled={disabled}
        readOnly={readOnly}
        error={!!error}
        dense={dense}
      >
        {value ? (
          <div
            className={cn(
              "flex flex-wrap gap-1 w-full",
              dense ? "p-1.5" : "p-2",
            )}
          >
            {value.values.map((v, idx) => (
              <Chip dense key={`${id}_${idx}`}>
                {v.label}
              </Chip>
            ))}
          </div>
        ) : (
          <div
            className={cn(
              "w-full flex items-center",
              dense ? "px-2 py-3 text-xs" : "px-2 py-4 text-sm",
              "text-front-400 dark:text-front-500",
            )}
          >
            <Typography.Span content={{ local: "Placeholder" }} />
          </div>
        )}
      </ButtonControl>
      {(helperKey || helper || keepHeight) && !error && (
        <LocalizationScope scope={localKey}>
          <Typography.P
            id={`${id}-helper`}
            className={cn("text-xs", dense ? "my-0.5" : "my-1")}
            color={ThemeColor.gray}
            content={helperKey ? { local: helperKey } : helper ? helper : null}
          >
            {" "}
          </Typography.P>
        </LocalizationScope>
      )}
      {error && (
        <LocalizationScope scope={[localKey, "::PSS"]}>
          <Typography.P
            id={`${id}-error`}
            className={cn("text-xs", dense ? "my-0.5" : "my-1")}
            color={ThemeColor.error}
            content={processErrorContent(error)}
          />
        </LocalizationScope>
      )}
      {renderDialog({
        open,
        onClose: handleClose,
        onClear: showClear && handleClear,
        onSelect: handleSelect,
        selectedIds: value?.values || [],
      })}
    </div>
  );
};

DialogMultiField.displayName = "FormField.DialogMulti";
